import http from "services/http";

export type PeriodKey =
  | "today"
  | "this_week"
  | "last_7_days"
  | "this_month"
  | "last_month"
  | "last_30_days"
  | "this_year";

export type ProductKey = "all" | "flight" | "ride" | "general";

export interface FinanceQuery {
  period?: PeriodKey;
  start?: string; // YYYY-MM-DD
  end?: string; // YYYY-MM-DD
  product?: ProductKey;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export const formatNaira = (value?: number | null) => {
  const n = Number(value || 0);
  const sign = n < 0 ? "-" : "";
  return `${sign}₦${Math.abs(n).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })}`;
};

// Strips empty values so the API applies its own defaults.
const clean = (params: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  );

export const getApiError = (error: any, fallback: string) =>
  error?.response?.data?.data?.error ||
  error?.response?.data?.message ||
  fallback;

// ---------- Finance page ----------

export interface Metric {
  amount: number;
  previous: number;
  changePercent: number | null;
}

export interface FinanceSummary {
  totalRevenue: Metric;
  totalExpenses: Metric;
  netProfit: Metric;
  profitMargin: { percent: number; previous: number };
  refunds: { paidToCustomers: number; receivedFromSuppliers: number };
  vendorPayables: { outstanding: number; count: number };
  needsAttention: {
    ridesMissingPayout: number;
    flightBookingsWithPostingErrors: number;
    ridesWithPostingErrors: number;
  };
}

export const getFinanceSummary = async (
  query: FinanceQuery,
): Promise<ApiResponse<FinanceSummary>> => {
  const res = await http.get("/admin/finance/summary", {
    params: clean({ ...query }),
  });
  return res.data;
};

export interface BreakdownItem {
  category: string;
  label: string;
  amount: number;
  count: number;
  sharePercent: number;
}

export interface BreakdownGroup {
  total: number;
  items: BreakdownItem[];
}

export const getFinanceBreakdown = async (
  query: FinanceQuery,
): Promise<ApiResponse<{ revenue: BreakdownGroup; expenses: BreakdownGroup }>> => {
  const res = await http.get("/admin/finance/breakdown", {
    params: clean({ ...query }),
  });
  return res.data;
};

export type Granularity = "daily" | "weekly" | "monthly";

export interface TrendPoint {
  bucket: string;
  label: string;
  revenue: number;
  expenses: number;
  netProfit: number;
}

export const getFinanceTrends = async (
  query: FinanceQuery & { granularity: Granularity },
): Promise<ApiResponse<{ series: TrendPoint[] }>> => {
  const res = await http.get("/admin/finance/trends", {
    params: clean({ ...query }),
  });
  return res.data;
};

export interface ActivityItem {
  id: string;
  reference: string;
  description: string;
  category: string;
  categoryLabel: string;
  type: "Revenue" | "Expense";
  amount: number;
  affectsPnL: boolean;
  product: string;
  bookingRef?: string;
  date: string;
  source: string;
  status: string;
  payoutStatus?: string;
  notes?: string;
  receipt?: string;
  createdBy?: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages?: number;
  totalPages?: number;
}

export interface ActivityQuery extends FinanceQuery {
  type?: "all" | "revenue" | "expense";
  search?: string;
  page?: number;
  limit?: number;
}

export const getFinanceActivity = async (
  query: ActivityQuery,
): Promise<ApiResponse<{ items: ActivityItem[]; pagination: Pagination }>> => {
  const res = await http.get("/admin/finance/activity", {
    params: clean({ ...query }),
  });
  return res.data;
};

export const exportFinance = async (query: ActivityQuery) => {
  const res = await http.get("/admin/finance/export", {
    params: clean({ ...query, page: undefined, limit: undefined }),
    responseType: "blob",
  });
  const disposition: string = res.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] || `finance-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export interface AddRevenuePayload {
  description: string;
  amount: number;
  date: string;
  notes?: string;
  bookingRef?: string;
}

export const addRevenue = async (payload: AddRevenuePayload) => {
  const res = await http.post("/admin/finance/revenue", clean({ ...payload }));
  return res.data;
};

export type ExpenseType =
  | "salaries"
  | "marketing"
  | "driver_payout"
  | "rent"
  | "software"
  | "operations"
  | "other";

export const EXPENSE_TYPES: { value: ExpenseType; label: string }[] = [
  { value: "salaries", label: "Salaries" },
  { value: "marketing", label: "Marketing" },
  { value: "driver_payout", label: "Driver Payout" },
  { value: "rent", label: "Rent" },
  { value: "software", label: "Software" },
  { value: "operations", label: "Operations" },
  { value: "other", label: "Other" },
];

export interface AddExpensePayload {
  expenseType: ExpenseType;
  description: string;
  amount: number;
  date: string;
  notes?: string;
  paidBy?: string;
}

export const addExpense = async (
  payload: AddExpensePayload,
  receipt?: File | null,
) => {
  if (receipt) {
    const formData = new FormData();
    Object.entries(clean({ ...payload })).forEach(([key, value]) =>
      formData.append(key, String(value)),
    );
    formData.append("receipt", receipt);
    const res = await http.post("/admin/finance/expenses", formData);
    return res.data;
  }
  const res = await http.post("/admin/finance/expenses", clean({ ...payload }));
  return res.data;
};

// ---------- Booking details drawer (flights) ----------

export interface RevenueLine {
  label: string;
  amount: number;
  applicable: boolean;
  status: "Paid" | "Not Applicable" | string;
  items: any[];
}

export interface FlightSegment {
  legs?: any[];
  [key: string]: any;
}

export interface FlightFinance {
  booking: {
    id: string;
    bookingId: string;
    pnr: string;
    airlinePnr?: string;
    bookingMode?: string;
    status: string;
    cancellationStatus?: string;
    financeStatus?: string;
    segments?: FlightSegment[];
  };
  posted: boolean;
  amountPaid: {
    amount: number;
    status: string;
    transactionReference?: string;
    paidAt?: string;
    ticketCost?: number;
  };
  revenue: {
    serviceCharge: RevenueLine;
    rescheduleRevenue: RevenueLine;
    addons: RevenueLine;
    cancellationRevenue: RevenueLine;
    wakanowCommission: RevenueLine;
    otherRevenue: RevenueLine;
  };
  deductions: Record<string, number>;
  cashMovements: { customerRefunds: number; supplierRefunds: number };
  totalBookingRevenue: number;
  totalDeductions: number;
  netBookingProfit: number;
  flags?: any;
  extraPaymentsAmount?: number;
  adjustments: any[];
  entries: any[];
}

export const getFlightFinance = async (
  id: string,
): Promise<ApiResponse<FlightFinance>> => {
  const res = await http.get(`/admin/dashboard/flight/${id}/finance`);
  return res.data;
};

export interface AddFlightAddonPayload {
  addon_id: string;
  amount?: number;
  date?: string;
  notes?: string;
}

export const addFlightAddon = async (
  id: string,
  payload: AddFlightAddonPayload,
) => {
  const res = await http.post(
    `/admin/dashboard/flight/${id}/addons`,
    clean({ ...payload }),
  );
  return res.data;
};

export type RescheduleType = "voluntary" | "airline";

export interface ReschedulePayload {
  rescheduleType: RescheduleType;
  legIndex?: number;
  newDepartureDate?: string;
  newDepartureTime?: string;
  newArrivalDate?: string;
  newArrivalTime?: string;
  clientPaid?: number;
  supplierCharge?: number;
  emailSubject?: string;
  emailBody?: string;
  sendEmail?: boolean;
  notes?: string;
}

export interface ReschedulePreview {
  rescheduleType: RescheduleType;
  clientPaid: number;
  supplierCharge: number;
  rescheduleRevenue: number;
  newDeparture?: string;
  newArrival?: string;
}

export const previewFlightReschedule = async (
  id: string,
  payload: ReschedulePayload,
): Promise<ApiResponse<ReschedulePreview>> => {
  const res = await http.post(
    `/admin/dashboard/flight/${id}/reschedule/preview`,
    clean({ ...payload }),
  );
  return res.data;
};

export const rescheduleFlight = async (
  id: string,
  payload: ReschedulePayload,
): Promise<
  ApiResponse<{
    adjustment: any;
    email: { sent: boolean; skipped: boolean; error?: string };
    finance: FlightFinance;
  }>
> => {
  const res = await http.post(
    `/admin/dashboard/flight/${id}/reschedule`,
    clean({ ...payload }),
  );
  return res.data;
};

export type CancelAction = "refund" | "cancellation";

export interface CancelPayload {
  action: CancelAction;
  refundToCustomer?: number;
  revenueRetained: number;
  reverseWakanowCommission?: boolean;
  emailSubject?: string;
  emailBody?: string;
  sendEmail?: boolean;
}

export interface CancelPreview {
  action: CancelAction;
  customer: string;
  pnr: string;
  amountPaid: number;
  refundToCustomer: number;
  revenue: number;
  supplierRefund: number;
  commissionReversed: number;
  profitImpact: number;
  errors: string[];
}

export const previewFlightCancel = async (
  id: string,
  payload: CancelPayload,
): Promise<ApiResponse<CancelPreview>> => {
  const res = await http.post(
    `/admin/dashboard/flight/${id}/cancel/preview`,
    clean({ ...payload }),
  );
  return res.data;
};

export const cancelFlight = async (id: string, payload: CancelPayload) => {
  const res = await http.post(
    `/admin/dashboard/flight/${id}/cancel`,
    clean({ ...payload }),
  );
  return res.data;
};
